/**
 * Transition Scene (The Fractal Breach)
 * UI collapse and peeling effect using tweens
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';

export class TransitionScene extends Phaser.Scene {
    constructor() {
        super({ key: 'TransitionScene' });
    }

    init(data) {
        this.uiElementsData = data.uiElements || [];
        this.score = data.score || 0;
        this.correctAnswers = data.correctAnswers || 0;
    }

    create() {
        console.log('TransitionScene started');

        // Dark background revealing
        this.cameras.main.setBackgroundColor(CONSTANTS.COLORS.DARK_BG);

        // Store falling objects
        this.fallingObjects = [];

        // Create the "peeling" visual effect
        this.createPeelingEffect();

        // Create abyss below
        this.createAbyss();

        // Create UI fragments that fall
        this.createFallingUIElements();

        // Add cracks spreading effect
        this.createCrackEffect();

        // Horror ambience
        audioEngine.startAmbientDrone();

        // Transition to gameplay after duration
        this.time.delayedCall(3000, () => {
            console.log('Starting gameplay...');
            this.startGameplay();
        });
    }

    createPeelingEffect() {
        // Several strips that will be animated falling
        this.peelStrips = [];

        for (let i = 0; i < 8; i++) {
            const strip = this.add.graphics();
            const y = i * 75;
            const height = 80;

            // Draw bright colored strip (simulating old UI)
            strip.fillStyle(CONSTANTS.COLORS.BRIGHT_BG, 0.95);
            strip.fillRect(0, 0, 800, height);

            // Add some "damage" texture
            strip.fillStyle(0xCCCCCC, 0.3);
            for (let j = 0; j < 10; j++) {
                strip.fillRect(
                    Math.random() * 800,
                    Math.random() * height,
                    Math.random() * 50 + 10,
                    3
                );
            }

            strip.y = y;

            // Animate strip falling with delay
            const delay = i * 150 + Math.random() * 100;

            this.tweens.add({
                targets: strip,
                y: 700 + Math.random() * 200,
                rotation: (Math.random() - 0.5) * 0.5,
                alpha: 0,
                duration: 2000,
                delay: delay,
                ease: 'Quad.in'
            });

            this.peelStrips.push(strip);
        }
    }

    createAbyss() {
        // Dark gradient abyss effect
        const abyss = this.add.graphics();

        // Multiple layers of darkness
        for (let i = 0; i < 10; i++) {
            const alpha = 0.1 + (i / 10) * 0.3;
            abyss.fillStyle(0x000000, alpha);
            abyss.fillRect(0, 500 + i * 15, 800, 20);
        }

        // Add some "void" particles floating up
        this.createVoidParticles();
    }

    createVoidParticles() {
        // Small particles rising from the abyss
        for (let i = 0; i < 20; i++) {
            const particle = this.add.circle(
                Math.random() * 800,
                600 + Math.random() * 100,
                Math.random() * 3 + 1,
                0x220022,
                0.6
            );

            this.tweens.add({
                targets: particle,
                y: -50,
                alpha: 0,
                duration: 3000 + Math.random() * 2000,
                delay: Math.random() * 2000,
                ease: 'Linear'
            });
        }
    }

    createFallingUIElements() {
        // Create visual representations of UI elements that fall
        this.uiElementsData.forEach((elementData, index) => {
            this.time.delayedCall(index * 200 + 500, () => {
                // Check if scene is still active
                if (this.scene && this.scene.isActive()) {
                    this.createFallingElement(elementData);
                }
            });
        });
    }

    createFallingElement(data) {
        // Safety check
        if (!this.add) return;

        // Create a container for the element
        const container = this.add.container(data.x, data.y);

        // Draw the element based on type
        const graphics = this.add.graphics();

        switch (data.type) {
            case 'banner':
                graphics.fillStyle(CONSTANTS.COLORS.CHEERFUL_ACCENT, 0.9);
                graphics.fillRoundedRect(
                    -data.width / 2, -data.height / 2,
                    data.width, data.height, 10
                );
                graphics.fillStyle(0xFFFFFF, 0.6);
                graphics.fillRect(-data.width / 3, -5, data.width * 0.6, 10);
                break;

            case 'question':
                graphics.fillStyle(0xFFFFFF, 0.95);
                graphics.fillRoundedRect(
                    -data.width / 2, -data.height / 2,
                    data.width, data.height, 16
                );
                graphics.lineStyle(3, CONSTANTS.COLORS.CHEERFUL_ACCENT);
                graphics.strokeRoundedRect(
                    -data.width / 2, -data.height / 2,
                    data.width, data.height, 16
                );
                graphics.fillStyle(0x333333, 0.5);
                graphics.fillRect(-30, -20, 60, 15);
                graphics.fillStyle(0x5C6BC0, 0.8);
                for (let i = 0; i < 4; i++) {
                    graphics.fillRoundedRect(-150 + i * 80, 20, 70, 35, 8);
                }
                break;

            case 'score':
            case 'progress':
                graphics.fillStyle(0xE0E0E0, 0.8);
                graphics.fillRoundedRect(
                    -data.width / 2, -data.height / 2,
                    data.width, data.height, 5
                );
                break;
        }

        container.add(graphics);

        // Add crack overlay
        this.addCrackToElement(container, data);

        // Animate falling using tweens
        this.tweens.add({
            targets: container,
            y: 800,
            rotation: Phaser.Math.FloatBetween(-0.5, 0.5),
            alpha: 0,
            duration: 1500,
            delay: 300,
            ease: 'Quad.in'
        });

        this.fallingObjects.push(container);
    }

    addCrackToElement(container, data) {
        const cracks = this.add.graphics();

        // Draw crack lines
        cracks.lineStyle(2, 0x000000, 0.6);

        // Random crack pattern
        const numCracks = Phaser.Math.Between(3, 6);
        const centerX = 0;
        const centerY = 0;

        for (let i = 0; i < numCracks; i++) {
            cracks.beginPath();
            cracks.moveTo(centerX, centerY);

            let x = centerX;
            let y = centerY;
            const angle = (i / numCracks) * Math.PI * 2 + Math.random() * 0.5;
            const length = Math.min(data.width, data.height) * 0.4;

            // Jagged line
            for (let j = 0; j < 4; j++) {
                x += Math.cos(angle + (Math.random() - 0.5) * 0.5) * (length / 4);
                y += Math.sin(angle + (Math.random() - 0.5) * 0.5) * (length / 4);
                cracks.lineTo(x, y);
            }

            cracks.strokePath();
        }

        container.add(cracks);

        // Flash effect
        this.cameras.main.flash(50, 255, 255, 255);
    }

    createCrackEffect() {
        // Large cracks spreading across the screen
        const cracks = this.add.graphics();
        cracks.setDepth(100);

        // Animate cracks spreading
        let crackProgress = 0;

        this.time.addEvent({
            delay: 50,
            repeat: 30,
            callback: () => {
                crackProgress += 0.033;
                this.drawScreenCracks(cracks, crackProgress);
            }
        });
    }

    drawScreenCracks(graphics, progress) {
        graphics.clear();
        graphics.lineStyle(3, 0x000000, 0.7);

        // Main crack from center
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height / 2;
        const maxLength = Math.min(this.scale.width, this.scale.height) * 0.5 * progress;

        // Multiple crack branches
        const branches = [
            { angle: -0.8, length: maxLength * 0.9 },
            { angle: -0.3, length: maxLength },
            { angle: 0.5, length: maxLength * 0.8 },
            { angle: 1.2, length: maxLength * 0.7 },
            { angle: 2.5, length: maxLength * 0.85 },
            { angle: -2.2, length: maxLength * 0.75 }
        ];

        branches.forEach(branch => {
            this.drawCrackBranch(graphics, centerX, centerY, branch.angle, branch.length);
        });

        // Secondary cracks (thinner)
        graphics.lineStyle(1.5, 0x000000, 0.5);
        branches.forEach(branch => {
            if (progress > 0.3) {
                const subLength = branch.length * 0.4;
                const startX = centerX + Math.cos(branch.angle) * branch.length * 0.5;
                const startY = centerY + Math.sin(branch.angle) * branch.length * 0.5;
                this.drawCrackBranch(graphics, startX, startY, branch.angle + 0.8, subLength);
                this.drawCrackBranch(graphics, startX, startY, branch.angle - 0.6, subLength);
            }
        });
    }

    drawCrackBranch(graphics, startX, startY, angle, length) {
        graphics.beginPath();
        graphics.moveTo(startX, startY);

        let x = startX;
        let y = startY;
        const segments = 8;

        for (let i = 0; i < segments; i++) {
            const segLength = length / segments;
            const jitter = (Math.random() - 0.5) * 0.4;
            x += Math.cos(angle + jitter) * segLength;
            y += Math.sin(angle + jitter) * segLength;
            graphics.lineTo(x, y);
        }

        graphics.strokePath();
    }

    startGameplay() {
        // Stop ambient drone (gameplay will start its own audio)
        audioEngine.stopAmbientDrone();

        // Fade out and start gameplay
        this.cameras.main.fadeOut(500, 0, 0, 0);

        this.time.delayedCall(500, () => {
            this.scene.start('GameplayScene', {
                score: this.score,
                correctAnswers: this.correctAnswers
            });
        });
    }

    update(time, delta) {
        // Nothing needed here now that we're using tweens
    }
}
